/* eslint-disable object-shorthand */
import {
	createActionsHook,
	createStateHook,
	createStore,
	type StoreActionApi,
} from 'react-sweet-state';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

export type EditorActionsContext = {
	reference: string;
	onRemove: () => void;
	sourceId: string;
};

export type EditorRovoButtonActionsState = {
	context?: EditorActionsContext;
	actions: ConversationAction[];
	/** Raw Atlassian Account ID (AAID) from the mention node. */
	agentMentionId: string | null;
	/** Counter incremented on each new agent mention insertion. */
	agentMentionInsertionCount: number;
	/** Resolved Agent Studio UUID for the mentioned agent. */
	resolvedAgentMentionId: string | null;
	/** Plain-text content of the block containing the mentioned agent. Snapshot of the block text at the time of the last mention insertion; not updated on subsequent keystrokes. */
	agentMentionContext: string | null;
	/** ProseMirror node type name of the block (e.g. 'taskItem', 'paragraph'). */
	agentMentionParentNodeType: string | null;
};

export const initialState: EditorRovoButtonActionsState = {
	actions: [],
	agentMentionId: null,
	agentMentionInsertionCount: 0,
	resolvedAgentMentionId: null,
	agentMentionContext: null,
	agentMentionParentNodeType: null,
};

export const actions = {
	setActions:
		(actions: ConversationAction[]) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			setState({ actions });
		},
	setContext:
		(context: EditorActionsContext | undefined) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			setState({ context: context });
		},
	removeContext:
		() =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			setState({ context: undefined });
		},
	removeActions:
		() =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			setState({ actions: [] });
		},
	setAgentMentionState:
		(
			state: Pick<
				EditorRovoButtonActionsState,
				| 'agentMentionId'
				| 'agentMentionInsertionCount'
				| 'agentMentionContext'
				| 'agentMentionParentNodeType'
			>,
		) =>
		({ setState, getState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			// Only reset resolvedAgentMentionId when the agent itself changes — not when
			// agentMentionContext updates (user typing after the mention) — to prevent the nudge
			// from flashing on every keystroke while the resolved UUID is already available.
			const current = getState();
			// Reset resolvedAgentMentionId when the agent changes OR when a new insertion
			// is made for the same agent — the insertion count increase means a new fetch
			// is about to start, and the old resolved UUID should not persist if it fails.
			const shouldResetResolved =
				state.agentMentionId !== current.agentMentionId ||
				state.agentMentionInsertionCount !== current.agentMentionInsertionCount;
			setState({
				...state,
				...(shouldResetResolved ? { resolvedAgentMentionId: null } : {}),
			});
		},
	setResolvedAgentMentionId:
		(resolvedAgentMentionId: string | null) =>
		({ setState }: StoreActionApi<EditorRovoButtonActionsState>) => {
			setState({ resolvedAgentMentionId });
		},
};

export const EditorRovoButtonActionsStore = createStore({
	initialState,
	actions,
	name: 'EditorRovoButtonActionsStore',
});

export const useEditorRovoButtonActions = createActionsHook(EditorRovoButtonActionsStore);
export const useEditorRovoButtonActionsActions = createStateHook(EditorRovoButtonActionsStore, {
	selector: (state: EditorRovoButtonActionsState) => state.actions,
});
export const useEditorRovoButtonActionsContext = createStateHook(EditorRovoButtonActionsStore, {
	selector: (state: EditorRovoButtonActionsState) => state.context,
});
export const useEditorRovoButtonAgentMentionId = createStateHook(EditorRovoButtonActionsStore, {
	selector: (state: EditorRovoButtonActionsState) => state.agentMentionId,
});
export const useEditorRovoButtonAgentMentionInsertionCount = createStateHook(
	EditorRovoButtonActionsStore,
	{
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionInsertionCount,
	},
);
export const useEditorRovoButtonResolvedAgentMentionId = createStateHook(
	EditorRovoButtonActionsStore,
	{
		selector: (state: EditorRovoButtonActionsState) => state.resolvedAgentMentionId,
	},
);
export const useEditorRovoButtonAgentMentionContext = createStateHook(
	EditorRovoButtonActionsStore,
	{
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionContext,
	},
);
export const useEditorRovoButtonAgentMentionParentNodeType = createStateHook(
	EditorRovoButtonActionsStore,
	{
		selector: (state: EditorRovoButtonActionsState) => state.agentMentionParentNodeType,
	},
);
